import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import useScrollAnimation from '../../hooks/useScrollAnimation';
import { businessMetrics, formatters } from '../../config/businessMetrics';

export default function ValuePropsSection() {
  const [hoveredCard, setHoveredCard] = useState(null);
  const { ref: sectionRef, isVisible } = useScrollAnimation();
  const navigate = useNavigate();

  const valueProps = [
    {
      id: 1,
      icon: (
        <svg className="w-9 h-9 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13 10V3L4 14h7v7l9-11h-7z"
          />
        </svg>
      ),
      title: 'Lightning Fast Results',
      description:
        'From launch to Letter of Intent in under 2-3 weeks. Our curated network of thousands of pre-qualified buyers and streamlined confidential process eliminates months of uncertainty.',
      color: 'from-amiva-primary to-blue-600',
      features: [
        'Curated buyer database of 10,000+ contacts',
        'Confidential outreach process',
        'Average LOI in 18 days',
      ],
      metric: '18',
      metricLabel: 'Days to LOI',
    },
    {
      id: 2,
      icon: (
        <svg className="w-9 h-9 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
          />
        </svg>
      ),
      title: 'Always There for You',
      description:
        'Our clients praise us most for being available 24/7 - even at 3am. This unwavering commitment ensures we always put your interests first, no matter the time or circumstance.',
      color: 'from-amiva-accent to-emerald-600',
      features: [
        '24/7 advisor availability',
        'Direct line to your dedicated team',
        'Client-first culture in everything we do',
      ],
      metric: '24/7',
      metricLabel: 'Availability',
    },
    {
      id: 3,
      icon: (
        <svg className="w-9 h-9 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
          />
        </svg>
      ),
      title: 'Complete Transparency',
      description:
        'Through AMIVA+, our AI-native platform, you have real-time visibility into every communication, NDA, LOI, and buyer interaction throughout your entire sale process.',
      color: 'from-amiva-slate to-slate-600',
      features: [
        'Live dashboard with real-time updates',
        'Every communication logged and visible',
        'AI-powered insights and analytics',
      ],
      metric: '100%',
      metricLabel: 'Transparency',
    },
  ];

  return (
    <div
      className="py-24 lg:py-32 relative overflow-hidden"
      style={{ backgroundColor: 'var(--color-gray-50)' }}
    >
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-5">
        <div
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%231E40AF' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        ></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-20 fade-in">
          <div className="mb-6">
            <span className="text-sm font-semibold text-amiva-primary uppercase tracking-wider">
              Why Business Owners Choose Us
            </span>
          </div>
          <h2 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6">
            The AMIVA <span className="text-gradient">Advantage</span>
          </h2>
          <p className="text-xl lg:text-2xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Our boutique approach combines cutting-edge technology with unparalleled personal
            service
          </p>
        </div>

        <div ref={sectionRef} className="grid grid-cols-1 lg:grid-cols-3 gap-10 items-stretch">
          {valueProps.map((prop, index) => (
            <div
              key={prop.id}
              className={`group transform transition-all duration-700 h-full ${
                isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
              }`}
              style={{
                animationDelay: `${index * 200}ms`,
                transitionDelay: `${index * 200}ms`,
              }}
              onMouseEnter={() => setHoveredCard(prop.id)}
              onMouseLeave={() => setHoveredCard(null)}
            >
              <div
                className={`card-premium p-10 h-full relative flex flex-col transform transition-all duration-500 ${
                  hoveredCard === prop.id
                    ? '-translate-y-4 shadow-2xl scale-[1.02]'
                    : 'hover:-translate-y-2 hover:shadow-xl'
                }`}
              >
                {/* Animated Metric Badge (hidden on small screens) */}
                <div
                  className={`hidden sm:block absolute top-6 right-6 transition-all duration-500 ${
                    hoveredCard === prop.id ? 'opacity-100 scale-100' : 'opacity-0 scale-0'
                  }`}
                >
                  <div
                    className={`bg-gradient-to-br ${prop.color} text-white px-4 py-2 rounded-xl shadow-lg`}
                  >
                    <div className="text-2xl font-bold">{prop.metric}</div>
                    <div className="text-xs opacity-90">{prop.metricLabel}</div>
                  </div>
                </div>

                {/* Icon with enhanced animation */}
                <div
                  className={`w-16 h-16 bg-gradient-to-br ${prop.color} rounded-2xl flex items-center justify-center mb-8 transform transition-all duration-500 ${
                    hoveredCard === prop.id
                      ? 'scale-125 rotate-6 shadow-2xl'
                      : 'group-hover:scale-110 group-hover:rotate-3'
                  }`}
                >
                  <div
                    className={`transition-transform duration-500 ${
                      hoveredCard === prop.id ? 'scale-110' : ''
                    }`}
                  >
                    {prop.icon}
                  </div>
                </div>

                <div className="min-h-[72px] flex items-end mb-4">
                  <h3
                    className={`text-2xl lg:text-3xl font-bold transition-colors duration-300 ${
                      hoveredCard === prop.id ? 'text-amiva-primary' : 'text-gray-900'
                    }`}
                  >
                    {prop.title}
                  </h3>
                </div>

                <p className="text-lg text-gray-600 mb-8 leading-relaxed min-h-[148px]">{prop.description}</p>

                {/* Animated Features */}
                <div className="space-y-4 min-h-[200px]">
                  {prop.features.map((feature, featureIndex) => (
                    <div
                      key={featureIndex}
                      className={`flex items-start min-h-[56px] transition-colors duration-300`}
                      style={{
                        transitionDelay: `${featureIndex * 100}ms`,
                      }}
                    >
                      <div
                        className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 transition-all duration-500 ${
                          prop.id === 1
                            ? 'bg-blue-100'
                            : prop.id === 2
                              ? 'bg-emerald-100'
                              : 'bg-slate-100'
                        }`}
                      >
                        <div
                          className={`w-2 h-2 rounded-full transition-all duration-500 ${
                            prop.id === 1
                              ? 'bg-amiva-primary'
                              : prop.id === 2
                                ? 'bg-amiva-accent'
                                : 'bg-amiva-slate'
                          } ${hoveredCard === prop.id ? 'animate-pulse' : ''}`}
                        ></div>
                      </div>
                      <span
                        className={`text-gray-700 ml-3 leading-6 transition-colors duration-300 flex-1 ${
                          hoveredCard === prop.id ? 'text-gray-900 font-medium' : ''
                        }`}
                      >
                        {feature}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Interactive Learn More - reserve space to avoid layout shift */}
                <div
                  className={`mt-auto h-12 transition-opacity duration-500 ${
                    hoveredCard === prop.id ? 'opacity-100' : 'opacity-0 pointer-events-none'
                  }`}
                >
                  <button
                    onClick={() => navigate('/valuation')}
                    className={`w-full bg-gradient-to-r ${prop.color} text-white py-3 rounded-xl font-semibold hover:shadow-lg transition-all transform hover:scale-105`}
                  >
                    Learn More →
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
